<template>
  <div class="relative w-full" :class="classes">
    <slot name="prefix" />
    <input
      :id="input_reference"
      :ref="input_reference"
      v-model="inputValue"
      :list="type == 'color' && colorOptions.length ? 'presetColors' : null"
      :name="name"
      :type="type == 'decimal' || type == 'currency' || type == 'percent' ? 'number' : type"
      v-bind="extraAttrs"
      :autocomplete="autocomplete"
      :class="[
        inputClasses,
        'focus:cursor-text peer dark:text-white dark:bg-dark-box-background w-full form-input disabled:bg-gray-100',
        {
          'bg-gray-50 dark:bg-dark-box-background': isBoxBackground,
          'hover:!bg-gray-100': highlightOnHover,
          'pl-9': icon,
          'w-5': type == 'color',
          'cursor-pointer placeholder-transparent': label,
          'border-status-error-red text-status-error-red': errorMessage || redMessage,
          'border-transparent focus:border-transparent focus:ring-0 shadow-none': noBorder,
          'py-1.5 mt-1': inTableFilter
        }
      ]"
      :disabled="disabled"
      :placeholder="noBorder ? placeholder : label ? ' ' : placeholder"
      @keydown="onKeyDown"
      @mouseup="mouseup"
      @input="onInputChange"
      @change="handleChange"
      @blur="onBlur">

    <label
      v-if="(showLabel && !noBorder) || (noBorder && !inputValue)"
      :ref="label_reference"
      :class="[
        { 'has-tooltip': show_tooltip },
        `${
          isBoxBackground
            ? 'bg-gray-100 dark:bg-dark-box-background h-1 mt-2.5'
            : label
              ? 'bg-light-label-background h-1 mt-2.5 dark:bg-dark-box-background'
              : ''
        }${errorMessage ? 'peer-placeholder-shown:text-status-error-red' : ''} ${icon ? 'ml-8' : ''} ${
          inTableFilter ? 'peer-placeholder-shown:top-3 peer-focus:-top-2' : ''
        }`
      ]"
      :for="input_reference"
      class="absolute max-w-full px-1 pb-2 text-sm font-medium transition-all rounded-md pointer-events-auto left-1 -top-3 text-common-grey peer-placeholder-shown:text-sm peer-placeholder-shown:text-gray-400 peer-placeholder-shown:top-2 peer-focus:-top-3 dark:text-dark-body-text peer-focus:font-normal peer-focus:text-blue">
      <span class="-mt-2.5 truncate overflow-ellipsis flex-nowrap flex">{{ label }}
        <div
          v-if="tooltipInfo"
          class="p-0.5 w-4 h-4 bg-[#899399] flex items-center justify-center rounded-full text-white ml-2 mt-0.5 has-tooltip">
          ?
          <span
            class="inline-flex ml-6 mt-14 text-common-grey items-center gap-2 px-2 py-1 text-sm font-medium rounded-md tooltip whitespace-nowrap bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
            {{ tooltipInfo }}</span>
        </div></span>
      <span class="bg-white tooltip">{{ label }}
        <div
          v-if="tooltipInfo"
          class="p-0.5 w-4 h-4 bg-[#899399] flex items-center justify-center text-white rounded-full ml-2 mt-0.5 has-tooltip">
          ?
          <span
            class="inline-flex text-common-grey ml-6 mt-14 items-center gap-2 px-2 py-1 text-sm font-medium rounded-md tooltip whitespace-nowrap bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
            {{ tooltipInfo }}</span>
        </div></span>
    </label>

    <div
      v-if="autocompleteOptions.length"
      :class="[autocompleteOptions.length ? 'opacity-100' : 'opacity-0', errorMessage && !meta.pending ? 'right-6' : 'right-0']"
      class="absolute inset-y-0 z-10 flex items-center pr-3 transition-all duration-500 cursor-pointer"
      @click="show_autocomplete_options = !show_autocomplete_options">
      <DynamicIcon
        :color="$store.state.ui.darkMode ? '#fff' : '#899399'"
        class="w-5 h-5 transition ease-in-out duration-side-menu-transition-duration" />
    </div>
    <div
      :class="[errorMessage && !meta.pending ? 'opacity-100' : 'opacity-0']"
      class="absolute inset-y-0 right-0 z-10 flex items-center pr-3 transition-all duration-500 pointer-events-none">
      <CircleX class="w-6 h-6 text-red-400" />
    </div>
    <div
      :class="[meta.valid && meta.dirty && show_success_check_mark && !meta.pending ? 'opacity-100' : 'opacity-0']"
      class="absolute inset-y-0 right-0 z-10 flex items-center pr-3 transition-all duration-500 pointer-events-none">
      <CircleCheck class="w-6 h-6 text-green-400" />
    </div>
    <div
      :class="[showPendingStatus && meta.pending ? 'opacity-100' : 'opacity-0']"
      class="absolute inset-y-0 right-0 z-10 flex items-center pr-3 transition-all duration-500 pointer-events-none">
      <span class="flex h-3 w-3 relative mr-1.5">
        <span class="absolute inline-flex w-full h-full bg-green-400 rounded-full animate-ping opacity-80" />
        <span class="relative inline-flex w-3 h-3 bg-green-400 rounded-full" />
      </span>
    </div>
    <transition
      v-show="autocompleteOptions.length && show_autocomplete_options"
      leave-active-class="transition duration-100 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0">
      <div
        static
        class="absolute z-20 w-full py-1 mt-1 overflow-auto text-base bg-white rounded-md shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm dark:bg-dark-box-background">
        <div v-for="item in autocompleteOptions" :key="item.value">
          <li
            class="relative flex items-center py-2 pl-2 pr-4 text-gray-900 cursor-pointer hover:bg-gray-200 dark:text-white dark:hover:bg-transparent"
            @click.prevent="handleSelectOption(item)">
            <span class="block ml-3 font-normal"> {{ item.label }} </span>
          </li>
        </div>
      </div>
    </transition>
    <p class="absolute right-0 text-red-400 first-letter:capitalize top-10 text-xsm" :class="[errorMessage ? 'visible' : 'invisible']">
      {{ errorMessage }}
    </p>
    <p
      v-if="redMessage"
      class="absolute right-0 text-red-400 first-letter:capitalize top-9 text-xsm"
      :class="[redMessage ? 'visible' : 'invisible']">
      {{ redMessage }}
    </p>
    <p v-if="infoMessage" class="mb-1 first-letter:capitalize absolute left-0 leading-tight pt-[1px] text-[12px] text-common-grey">
      {{ infoMessage }}
    </p>
    <slot name="postfix" />
  </div>
</template>

<script>
import { useField } from "vee-validate"
import { CircleCheck, CircleX } from "lucide-vue-next"
import DynamicIcon from "@/components/shared/icons/DynamicIcon.vue"
import { toRef } from "vue"
import client from "@/services/http.client"

export default {
  components: {
    DynamicIcon,
    CircleX,
    CircleCheck
  },
  props: {
    tooltipInfo: null,
    maxValue: {
      type: Number
    },
    minValue: {
      type: Number
    },

    highlightOnHover: {
      type: Boolean,
      default: false
    },
    colorOptions: {
      type: Array,
      default: () => []
    },
    isBoxBackground: {
      type: Boolean,
      default: false
    },
    icon: null,
    noBorder: {
      type: Boolean,
      default: false
    },
    name: {
      type: String,
      required: true
    },
    label: {
      type: String,
      default: null,
      required: false
    },
    showLabel: {
      type: Boolean,
      default: true
    },
    classes: {
      type: String,
      default: ""
    },
    inputClasses: {
      type: String,
      default: ""
    },
    type: {
      type: String,
      default: "text"
    },
    placeholder: {
      type: String,
      default: "Description"
    },
    infoMessage: {
      type: String,
      default: ""
    },
    redMessage: {
      type: String,
      default: ""
    },
    autocomplete: {
      type: String,
      default: "off"
    },
    value: {
      type: [String, Number]
    },

    disabled: {
      type: Boolean,
      default: false
    },
    showSuccessCheckMark: {
      type: Boolean,
      default: false
    },
    hideSuccessCheckMarkAfter: {
      type: Number,
      default: null
    },
    validateOnInput: {
      type: Boolean,
      default: false
    },
    showPendingStatus: {
      type: Boolean,
      default: false
    },
    autoFocus: {
      type: Boolean,
      default: false
    },
    debounce: {
      type: Number,
      default: 0
    },
    extraAttrs: {
      type: Object,
      default: () => {}
    },
    inTableFilter: {
      type: Boolean,
      default: false
    },
    // inputOptions: {
    //   type: Object,
    //   default: () => {}
    // },
    keydown: {
      type: Function,
      default: () => () => {}
    },
    mouseup: {
      type: Function,
      default: () => () => {}
    },
    onlyAlphaNumeric: {
      type: Boolean,
      default: false
    },
    autocompleteOptions: {
      type: Array,
      required: false,
      default: () => []
    },
    validationEndpoint: {
      type: String,
      required: false
    },
    validationMethod: {
      type: String,
      default: "GET"
    },
    focus: {
      type: Boolean,
      default: false
    }
  },

  emits: ["update:modelValue", "blurred", "enter"],

  setup(props) {
    const name = toRef(props, "name")
    const {
      value: inputValue,
      errorMessage,
      handleBlur,
      meta,
      handleChange,
      setErrors
    } = useField(name, undefined, {
      initialValue: props.value
    })

    const input_reference = `ref-i-${Math.random().toString(36).substring(7)}`
    const label_reference = input_reference.replace("ref-i-", "ref-l-")
    return {
      input_reference,
      label_reference,
      errorMessage,
      handleChange,
      handleBlur,
      inputValue,
      setErrors,
      meta
    }
  },
  data() {
    return {
      show_autocomplete_options: false,
      show_tooltip: false,
      show_success_check_mark: false,
      change_timeout: null,
      last_value: null,
      prevent_watch: false
    }
  },
  watch: {
    focus() {
      if (this.focus) {
        this.$refs[this.input_reference].focus()
      }
    },
    value() {
      this.inputValue = this.value
    },
    inputValue() {
      const alphanumeric_pattern = /^[a-zA-Z0-9]+$/
      if (
        ((this.maxValue !== undefined ? parseInt(this.inputValue) > this.maxValue : false) ||
          (this.minValue !== undefined ? parseInt(this.inputValue) < this.minValue : false)) &&
        this.inputValue !== ""
      ) {
        this.prevent_watch = true
        this.inputValue = this.last_value
        setTimeout(() => {
          this.prevent_watch = false
        }, 200)
      } else if (this.onlyAlphaNumeric && !alphanumeric_pattern.test(this.inputValue)) {
        this.prevent_watch = true
        this.inputValue = this.last_value
        setTimeout(() => {
          this.prevent_watch = false
        }, 200)
      } else {
        this.last_value = this.inputValue
      }
    }
  },
  mounted() {
    if (this.$refs[this.label_reference]?.scrollWidth > this.$refs[this.input_reference]?.scrollWidth) {
      this.show_tooltip = true
    }
    if (this.autoFocus) {
      setTimeout(() => {
        if (this.$refs[this.input_reference]) {
          this.$refs[this.input_reference].focus()
        }
        if (this.$refs[this.label_reference]) {
          this.$refs[this.label_reference].click()
        }
      }, 300)
    }
  },
  created() {
    this.show_success_check_mark = this.showSuccessCheckMark
  },

  methods: {
    onKeyDown(event) {
      if (event.key === "Enter") {
        this.$emit("enter")
      }
      this.keydown(event)
    },
    toggleCheckMark() {
      if (this.showSuccessCheckMark) {
        this.show_success_check_mark = true
        if (this.hideSuccessCheckMarkAfter != null) {
          setTimeout(() => {
            this.show_success_check_mark = false
          }, this.hideSuccessCheckMarkAfter)
        }
      }
    },
    onInputChange(e) {
      if (this.validateOnInput) {
        clearTimeout(this.change_timeout)
        this.change_timeout = setTimeout(() => {
          this.handleChange(e)
          this.$emit("update:modelValue", this.inputValue)
          if (this.validationEndpoint) {
            this.validateInput(this.inputValue)
              .then(() => {
                this.toggleCheckMark()
              })
              .catch((err) => {
                // mark as invalid
                this.setErrors(err.response.data.description)
              })
          } else {
            this.toggleCheckMark()
          }
        }, this.debounce)
      }
    },
    handleSelectOption(item) {
      this.inputValue = this.inputValue.toString() + " " + item.value
      this.$emit("update:modelValue", this.inputValue)
      this.show_autocomplete_options = false
    },
    onBlur(e) {
      this.show_autocomplete_options = false
      this.$emit("update:modelValue", this.inputValue)
      this.handleBlur(e)
      this.$emit("blurred")
      this.toggleCheckMark()
    },
    validateInput(value) {
      const payload = {
        [this.name]: value
      }
      if (this.validationMethod === "GET") {
        return client({
          method: this.validationMethod,
          url: `${this.validationEndpoint}?${this.name}=${value}`
        })
      } else {
        return client({
          method: this.validationMethod,
          url: this.validationEndpoint,
          data: payload
        })
      }
    }
  }
}
</script>

<style scoped>
.close-btn:after {
  display: inline-block;
  content: "\00d7"; /* This will render the 'X' */
}
</style>
